<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '@/store/notifications'
import { useUserStore } from '@/store/user'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import { DBtn, DLoading, DSelectFieldSearch } from 'front-library'
import {
  getWorkspaces,
  setWorkspaceOverride,
  WorkspaceItem
} from '@/app/core/services/workspace'

usePayrollChange(() => window.location.reload())

const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

const loading = ref(false)
const messageLoading = ref('')

const itemsWorkspaces = ref<WorkspaceItem[]>([])
const searchWorkspace = ref('')
const selectedWorkspaceId = ref('')

const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = (items ?? []).filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

const back = () => {
  void router.back()
}

const onSubmit = async () => {
  if (!selectedWorkspaceId.value) {
    notifications.addNotification({
      title: t('notification.error'),
      text: t('workspace.override.selectRequired'),
      type: 'error',
      time: 10000
    })
    return
  }

  loading.value = true
  messageLoading.value = t('loading.data')

  try {
    const saved = await setWorkspaceOverride({
      overrideWorkspaceId: selectedWorkspaceId.value
    })

    loading.value = false

    if (saved) {
      notifications.addNotification({
        title: t('notification.edited'),
        text: t('workspace.override.saved'),
        type: 'success',
        time: 10000
      })
      store.logout()
      void router.push({ name: 'Login' })
    }
  } catch (error: any) {
    notificationError(error, 'workspace', t, notifications, loading)
  }
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')

  try {
    itemsWorkspaces.value = await getWorkspaces()

    const currentWorkspaceId = store.user?.workspaceId
    const exists = currentWorkspaceId
      ? itemsWorkspaces.value.some(w => w.id === currentWorkspaceId)
      : false

    if (exists && currentWorkspaceId) {
      selectedWorkspaceId.value = currentWorkspaceId
    } else if (itemsWorkspaces.value.length > 0) {
      selectedWorkspaceId.value = itemsWorkspaces.value[0].id
    }

    loading.value = false
  } catch (error: any) {
    notificationError(error, 'workspace', t, notifications, loading)
  }
})
</script>

<template>
  <DLoading v-show="loading" :message="messageLoading" />

  <div class="h-full px-5">
    <div class="bg-gray-50 pb-2 sm:pb-4">
      <div class="leading-4 pt-responsive">
        <p class="text-3xl font-semibold text-shadow text-blue-900">
          {{ t('workspace.override.title') }}
        </p>
        <p class="text-gray-500 text-shadow">
          {{ t('workspace.override.description') }}
        </p>
      </div>
    </div>

    <form class="pt-responsive" @submit.prevent="onSubmit">
      <div
        class="max-w-2xl mx-auto bg-white rounded-md border border-gray-200 shadow-md">
        <div class="p-6">
          <DSelectFieldSearch
            class="w-full"
            :label="t('workspace.override.selectLabel')"
            :options="filterOptions(itemsWorkspaces, searchWorkspace)"
            @filter="searchWorkspace = $event"
            value-key="id"
            :required="true"
            v-model="selectedWorkspaceId"
            :placeholder="t('workspace.override.selectPlaceholder')" />

          <p class="text-xs text-gray-500 mt-2">
            {{ t('workspace.override.helperSelect') }}
          </p>
        </div>

        <div class="p-5">
          <hr class="pb-5" />
          <div class="flex justify-end gap-2 items-center">
            <DBtn
              type="button"
              class="font-semibold py-1 text-base"
              color="secondary"
              @click="back()">
              {{ t('form.button.cancel') }}
            </DBtn>

            <DBtn
              v-if="store.hasPermission('user:set_workspace')"
              class="font-semibold py-1 text-base"
              color="blue">
              {{ t('form.button.save') }}
            </DBtn>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>
